import type { ForwardedRef } from 'react'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import Measure from 'react-measure'
import { RecorderWaveform, getWaveWidth } from '@mote/ui'
import { scrubberMaxWidth } from './Common.styles'

export interface MeasuredAudioWaveformRecorderProps {
  stream: MediaStreamAudioSourceNode
  audioContext: AudioContext
  recording: boolean
}

export function MeasuredAudioWaveformRecorder(
  props: MeasuredAudioWaveformRecorderProps
) {
  return (
    <Measure bounds>
      {measureProps => (
        <AudioWaveformRecorder
          ref={measureProps.measureRef}
          width={measureProps.contentRect.bounds?.width ?? scrubberMaxWidth}
          {...props}
        />
      )}
    </Measure>
  )
}

type Milliseconds = number

const interval: Milliseconds = 20

export interface AudioWaveformRecorderProps
  extends MeasuredAudioWaveformRecorderProps {
  stream: MediaStreamAudioSourceNode
  audioContext: AudioContext
  width: number
}

export const AudioWaveformRecorder = forwardRef(function AudioWaveformRecorder(
  props: AudioWaveformRecorderProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const numberOfBarsRef = useRef(0)
  numberOfBarsRef.current = Math.round(props.width / getWaveWidth('l'))

  const analyzer = useMemo(() => {
    if (!props.audioContext) return null

    const analyzer = props.audioContext.createAnalyser()
    analyzer.fftSize = 32
    analyzer.smoothingTimeConstant = interval / 100
    return analyzer
  }, [props.audioContext])

  const frequencyBuffer = useMemo(
    () =>
      analyzer ? new Uint8Array(analyzer.frequencyBinCount) : new Uint8Array(0),
    [analyzer]
  )
  const [currentWaveform, setCurrentWaveform] = useState<number[]>(
    Array.from(new Uint8Array(numberOfBarsRef.current).fill(-1))
  )

  const _ = useRef(currentWaveform)
  _.current = currentWaveform

  const streamRef = useRef<MediaStreamAudioSourceNode>()
  useEffect(() => {
    if (!props.stream || !analyzer) {
      return
    }

    streamRef.current = props.stream
    streamRef.current.connect(analyzer)
  }, [props.stream, analyzer])

  useEffect(() => {
    if (!props.recording || !analyzer) {
      return
    }

    const smoother = new Smoother(
      analyzer,
      frequencyBuffer,
      numberOfBarsRef.current
    )
    const intervalId = window.setInterval(() => {
      const [next, ready] = smoother.tryGet()
      if (ready) {
        setCurrentWaveform([
          ..._.current.slice(-(numberOfBarsRef.current - 1)),
          next
        ])
      }
    }, interval)
    return () => window.clearInterval(intervalId)
  }, [props.recording, analyzer])

  return (
    <RecorderWaveform
      ref={ref}
      waveformData={currentWaveform}
      numberOfBars={numberOfBarsRef.current}
    />
  )
})

export class Smoother {
  smoothingWindowCount = 5
  smoothingWindow = new Array(this.smoothingWindowCount).fill(0)
  factor = 0.9
  smoothingWindowCounter = 0
  analyzer: AnalyserNode
  frequencyBuffer: Uint8Array
  width: number

  constructor(
    analyzer: AnalyserNode,
    frequencyBuffer: Uint8Array,
    width: number
  ) {
    this.analyzer = analyzer
    this.frequencyBuffer = frequencyBuffer
    this.width = width
  }

  tryGet(): [next: number, ready: true] | [next: undefined, ready: false] {
    this.analyzer.getByteFrequencyData(this.frequencyBuffer)
    let sum = 0
    for (let i = 0; i < this.frequencyBuffer.length; i++) {
      sum += (this.frequencyBuffer[i] / (255 * this.factor)) ** 2
    }
    this.smoothingWindow[this.smoothingWindowCounter] = Math.sqrt(
      sum / this.frequencyBuffer.length
    )
    this.smoothingWindowCounter += 1
    if (this.smoothingWindowCount === this.smoothingWindowCounter) {
      this.smoothingWindowCounter = 0
      return [Math.min(1, Math.max(...this.smoothingWindow)), true]
    }
    return [undefined, false]
  }
}
